import React, { useState, useEffect } from 'react';
import { 
  X, Download, Printer, Calendar, RefreshCw, FileText, Filter, Clock
} from 'lucide-react';
import '../styles/ReportsForm.css';
import apiClient from '../services/api';
import { useNavigate } from 'react-router-dom';

const TrialBalance = ({ onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter state
  const [filters, setFilters] = useState({
    AsOnDate: new Date().toISOString().slice(0, 10),
    ShowZeroBalance: false,
    AccountGroup: ''
  });
  
  // Report data
  const [reportData, setReportData] = useState(null);
  const [accountGroups, setAccountGroups] = useState([]);
  
  // Session
  const session = getSessionData();
  
  function getSessionData() {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session:', error);
      return null;
    }
  }
  
  useEffect(() => {
    loadAccountGroups();
  }, []);
  
  const loadAccountGroups = async () => {
    try {
      const response = await apiClient.get('/AccountGroups.php');
      if (response.data.success) {
        setAccountGroups(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading account groups:', err);
    }
  };
  
  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const handleGenerateReport = async () => {
    if (!filters.AsOnDate) {
      setError('Please select a date');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.get('/TrialBalance.php', { params: filters });
      
      if (response.data.success) {
        setReportData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Error generating report: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleExportCSV = () => {
    if (!reportData || !reportData.accounts || reportData.accounts.length === 0) {
      alert('No data to export');
      return;
    }
    
    const headers = ['Account Code', 'Account Name', 'Group', 'Opening Balance', 'Debit', 'Credit', 'Closing Balance', 'Dr/Cr'];
    
    const rows = reportData.accounts.map(acc => [
      acc.ACHeadCode || '',
      acc.ACHeadName || '',
      acc.GroupName || '',
      acc.OpeningBalance || '0.00',
      acc.DebitTotal || '0.00',
      acc.CreditTotal || '0.00',
      acc.ClosingBalance || '0.00',
      acc.BalanceType || 'Dr'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `trial_balance_${filters.AsOnDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const handleClearFilters = () => {
    setFilters({
      AsOnDate: new Date().toISOString().slice(0, 10),
      ShowZeroBalance: false,
      AccountGroup: ''
    });
    setReportData(null);
  };
  
  if (!session?.companyid) {
    return (
      <div className="report-container">
        <div className="report-header">
          <div className="header-left">
            <h2>Session Required</h2>
            <p className="subtitle">Please select company and year</p>
          </div>
        </div>
        <div className="report-display">
          <div className="report-empty">
            <div className="empty-icon">🔒</div>
            <h3 className="empty-title">Session Required</h3>
            <p className="empty-message">Please select a company and financial year from the dashboard.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="report-container">
      {/* Header */}
      <div className="report-header">
        <div className="header-left">
          <h2>Trial Balance</h2>
          <p className="subtitle">Complete Account Summary Report</p>
        </div>
      </div>
      
      {/* Toolbar */}
      <div className="report-toolbar no-print">
        <button onClick={handleGenerateReport} disabled={loading} className="btn-generate">
          <RefreshCw size={18} />
          Generate Report
        </button>
        <button 
          onClick={handlePrint} 
          disabled={!reportData || loading}
          className="btn-print"
        >
          <Printer size={18} />
          Print
        </button>
        <button 
          onClick={handleExportCSV} 
          disabled={!reportData || loading}
          className="btn-export"
        >
          <Download size={18} />
          Export CSV
        </button>
        <button onClick={handleClearFilters} disabled={loading} className="btn-clear">
          <RefreshCw size={18} />
          Clear
        </button>
      </div>
      
      {/* Alerts */}
      {error && (
        <div className="report-alert alert-error">
          <div className="alert-icon">⚠</div>
          <div><strong>Error:</strong> {error}</div>
        </div>
      )}
      
      {/* Filters */}
      <div className="filters-section no-print">
        <h3 className="section-title">
          <Filter size={18} />
          Report Filters
        </h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label>As On Date <span className="required">*</span></label>
            <input
              type="date"
              name="AsOnDate"
              value={filters.AsOnDate}
              onChange={handleFilterChange}
              required
            />
          </div>
          
          <div className="filter-group">
            <label>Account Group</label>
            <select
              name="AccountGroup"
              value={filters.AccountGroup}
              onChange={handleFilterChange}
            >
              <option value="">All Groups</option>
              {accountGroups.map(group => (
                <option key={group.acgroupid} value={group.acgroupid}>
                  {group.acgroupname} ({group.actype})
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <div className="checkbox-filter">
              <input
                type="checkbox"
                name="ShowZeroBalance"
                id="ShowZeroBalance"
                checked={filters.ShowZeroBalance}
                onChange={handleFilterChange}
              />
              <label htmlFor="ShowZeroBalance">
                Show Zero Balance Accounts
              </label>
            </div>
          </div>
        </div>
      </div>
      
      {/* Loading State */}
      {loading && (
        <div className="report-loading">
          <div className="loading-spinner"></div>
          <p className="loading-text">Generating trial balance...</p>
        </div>
      )}
      
      {/* Report Display */}
      {reportData && !loading && (
        <div className="report-display">
          {/* Report Header */}
          <div className="report-title-section">
            <h1 className="report-company-name">
              {reportData.company?.CompanyName || 'Company Name'}
            </h1>
            <h2 className="report-name">Trial Balance</h2>
            <div className="report-metadata">
              <div className="report-metadata-item">
                <Calendar size={16} />
                <span>As On: {new Date(filters.AsOnDate).toLocaleDateString()}</span>
              </div>
              <div className="report-metadata-item">
                <FileText size={16} />
                <span>Financial Year: {reportData.year?.YearName || 'N/A'}</span>
              </div>
              <div className="report-metadata-item">
                <Clock size={16} />
                <span>Generated: {new Date().toLocaleString()}</span>
              </div>
            </div>
          </div>
          
          {/* Trial Balance Table */}
          {reportData.accounts && reportData.accounts.length > 0 ? (
            <div className="report-table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: '80px' }}>Code</th>
                    <th>Account Name</th>
                    <th>Group</th>
                    <th className="text-right" style={{ width: '120px' }}>Opening Bal</th>
                    <th className="text-right" style={{ width: '120px' }}>Debit</th>
                    <th className="text-right" style={{ width: '120px' }}>Credit</th>
                    <th className="text-right" style={{ width: '120px' }}>Closing Bal</th>
                    <th className="text-center" style={{ width: '60px' }}>Dr/Cr</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.accounts.map((account, index) => (
                    <tr key={index}>
                      <td>
                        <span className="account-code">{account.ACHeadCode}</span>
                      </td>
                      <td>
                        <span className="account-name">{account.ACHeadName}</span>
                      </td>
                      <td style={{ fontSize: '13px', color: '#6b7280' }}>
                        {account.GroupName}
                      </td>
                      <td className="text-right">
                        {parseFloat(account.OpeningBalance || 0).toFixed(2)}
                      </td>
                      <td className="text-right amount-debit">
                        {parseFloat(account.DebitTotal || 0).toFixed(2)}
                      </td>
                      <td className="text-right amount-credit">
                        {parseFloat(account.CreditTotal || 0).toFixed(2)}
                      </td>
                      <td className="text-right font-bold">
                        {parseFloat(account.ClosingBalance || 0).toFixed(2)}
                      </td>
                      <td className="text-center">
                        <span className={`balance-badge ${account.BalanceType === 'Dr' ? 'dr' : 'cr'}`}>
                          {account.BalanceType || 'Dr'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4" className="total-label">Totals:</td>
                    <td className="text-right total-amount amount-debit">
                      {parseFloat(reportData.totals?.TotalDebit || 0).toFixed(2)}
                    </td>
                    <td className="text-right total-amount amount-credit">
                      {parseFloat(reportData.totals?.TotalCredit || 0).toFixed(2)}
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                  
                  {/* Difference (if any) */}
                  {reportData.totals?.Difference && parseFloat(reportData.totals.Difference) !== 0 && (
                    <tr>
                      <td colSpan="6" className="total-label" style={{ color: '#991b1b' }}>
                        Difference:
                      </td>
                      <td className="text-right total-amount" style={{ color: '#991b1b' }}>
                        {parseFloat(reportData.totals.Difference).toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="report-empty">
              <div className="empty-icon">📊</div>
              <h3 className="empty-title">No Accounts Found</h3>
              <p className="empty-message">No accounts found for the selected filters</p>
            </div>
          )}
          
          {/* Balance Check Notice */}
          {reportData.totals && (
            <div className={`balance-check ${parseFloat(reportData.totals.Difference || 0) === 0 ? 'balanced' : 'not-balanced'}`}>
              <div className="balance-check-icon">
                {parseFloat(reportData.totals.Difference || 0) === 0 ? '✓' : '⚠'}
              </div>
              <div className="balance-check-content">
                {parseFloat(reportData.totals.Difference || 0) === 0 ? (
                  <>
                    <h3 className="balance-check-title">Trial Balance is Balanced!</h3>
                    <p className="balance-check-details">
                      Total Debit = Total Credit = {parseFloat(reportData.totals.TotalDebit || 0).toFixed(2)}
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="balance-check-title">Trial Balance Not Balanced!</h3>
                    <p className="balance-check-details">
                      Difference: {parseFloat(reportData.totals.Difference || 0).toFixed(2)}
                    </p>
                    <p className="balance-check-details" style={{ fontSize: '13px', marginTop: '4px' }}>
                      Please check your journal entries for errors.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
          
          {/* Signature Section (for print) */}
          <div className="signature-section print-only">
            <div className="signature-box">
              <div className="signature-line">Prepared By</div>
            </div>
            <div className="signature-box">
              <div className="signature-line">Checked By</div>
            </div>
            <div className="signature-box">
              <div className="signature-line">Approved By</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrialBalance;